import React, { useState, useMemo } from "react";

// --- DATA MOCKS v5 (Simulating AI-retrieved data) ---
// Now includes Tannin Quality, Temperature, Aroma Compounds, and Problem Ingredients.

const menuItemsData = [
  {
    id: 1,
    name: "Grilled Ribeye Steak",
    profile: {
      fat: 9,
      acidity: 1,
      sweetness: 1,
      bitterness: 4,
      umami: 8,
      spice: 0,
      intensity: 9,
      texture: "chewy",
      temp: "hot",
      cookingMethod: "grilling",
      aromaCompounds: ["maillard", "smoke"],
      problemIngredients: [],
    },
  },
  {
    id: 2,
    name: "Steamed Artichoke with Butter",
    profile: {
      fat: 4,
      acidity: 2,
      sweetness: 1,
      bitterness: 6,
      umami: 3,
      spice: 0,
      intensity: 4,
      texture: "soft",
      temp: "hot",
      cookingMethod: "steaming",
      aromaCompounds: ["vegetal"],
      problemIngredients: ["cynarin"],
    },
  },
  {
    id: 3,
    name: "Fried Artichoke Hearts",
    profile: {
      fat: 7,
      acidity: 3,
      sweetness: 1,
      bitterness: 4,
      umami: 4,
      spice: 0,
      intensity: 5,
      texture: "crispy",
      temp: "hot",
      cookingMethod: "frying",
      aromaCompounds: ["maillard"],
      problemIngredients: ["cynarin"],
    },
  },
  {
    id: 4,
    name: "Spicy Thai Green Curry",
    profile: {
      fat: 7,
      acidity: 4,
      sweetness: 3,
      bitterness: 2,
      umami: 7,
      spice: 7,
      intensity: 8,
      texture: "creamy",
      temp: "hot",
      cookingMethod: "stewing",
      aromaCompounds: ["linalool", "gingerol"],
      problemIngredients: [],
    },
  },
  {
    id: 5,
    name: "Sauvignon Blanc & Goat Cheese Salad",
    profile: {
      fat: 5,
      acidity: 8,
      sweetness: 1,
      bitterness: 3,
      umami: 4,
      spice: 0,
      intensity: 6,
      texture: "creamy",
      temp: "chilled",
      cookingMethod: "raw",
      aromaCompounds: ["pyrazines", "capric_acid"],
      problemIngredients: [],
    },
  },
];

const winesData = [
  {
    id: 1,
    name: "Young Cabernet Sauvignon",
    profile: {
      acidity: 6,
      sweetness: 1,
      tannin: 9,
      tanninQuality: 0.7,
      alcohol: 8,
      intensity: 9,
      mouthfeel: "astringent",
      idealTemp: 18,
      currentTemp: 18,
      aromaCompounds: ["cassis", "bell_pepper"],
    },
  },
  {
    id: 2,
    name: "Fine Aged Bordeaux",
    profile: {
      acidity: 7,
      sweetness: 1,
      tannin: 7,
      tanninQuality: 0.95,
      alcohol: 7,
      intensity: 8,
      mouthfeel: "silky",
      idealTemp: 18,
      currentTemp: 18,
      aromaCompounds: ["smoke", "leather"],
    },
  },
  {
    id: 3,
    name: "Loire Sauvignon Blanc",
    profile: {
      acidity: 9,
      sweetness: 1,
      tannin: 0,
      tanninQuality: 1.0,
      alcohol: 5,
      intensity: 5,
      mouthfeel: "crisp",
      idealTemp: 8,
      currentTemp: 8,
      aromaCompounds: ["pyrazines", "thiols"],
    },
  },
  {
    id: 4,
    name: "Off-Dry Riesling",
    profile: {
      acidity: 9,
      sweetness: 5,
      tannin: 0,
      tanninQuality: 1.0,
      alcohol: 4,
      intensity: 6,
      mouthfeel: "light",
      idealTemp: 9,
      currentTemp: 9,
      aromaCompounds: ["linalool", "terpenes"],
    },
  },
  {
    id: 5,
    name: "Fino Sherry",
    profile: {
      acidity: 7,
      sweetness: 1,
      tannin: 1,
      tanninQuality: 1.0,
      alcohol: 8,
      intensity: 6,
      mouthfeel: "crisp",
      idealTemp: 7,
      currentTemp: 7,
      aromaCompounds: ["acetaldehyde", "saline"],
    },
  },
];

// --- ADVANCED ALGORITHM v5 ---
const getAdvancedPairingScore_v5 = (foodProfile, wineProfile) => {
  let score = 0;
  const rationale = [];
  if (!foodProfile || !wineProfile) return { score: 0, rationale: [] };

  // --- Base Component Matching ---
  if (foodProfile.fat > 5 && wineProfile.acidity > 6) {
    score += 7;
    rationale.push({
      p: "+7",
      r: "Palate Cleansing: Acidity cuts through richness.",
    });
  }
  if (foodProfile.spice > 4 && wineProfile.sweetness > 3) {
    score += 7;
    rationale.push({
      p: "+7",
      r: "Spice Soothing: Sweetness cools spice heat.",
    });
  }
  if (Math.abs(foodProfile.intensity - wineProfile.intensity) <= 2) {
    score += 5;
    rationale.push({ p: "+5", r: "Intensity Match: Well-balanced power." });
  }
  if (wineProfile.acidity < foodProfile.acidity) {
    score -= 15;
    rationale.push({ p: "-15", r: "VETO - Acid Flattens: Wine tastes flat." });
  }
  if (wineProfile.sweetness < foodProfile.sweetness) {
    score -= 15;
    rationale.push({ p: "-15", r: "VETO - Sweet Dries: Wine tastes bitter." });
  }
  if (foodProfile.isOilyFish && wineProfile.tannin > 4) {
    score -= 20;
    rationale.push({
      p: "-20",
      r: "VETO - Fishy Metal: Tannins and fish oils create a metallic taste.",
    });
  }

  // --- 1. Tannin Quality Index (TQI) Logic ---
  const qualityModifier = 1 - (1 - wineProfile.tanninQuality) * 0.75; // Perfect quality = 1.0, worst quality = 0.8125
  if (foodProfile.fat > 7 && wineProfile.tannin > 7) {
    let s = 10 * qualityModifier;
    score += s;
    rationale.push({
      p: `+${s.toFixed(0)}`,
      r: "Synergistic Pairing: Rich food softens tannins. (Bonus adjusted by tannin quality)",
    });
  }
  if (foodProfile.umami > 6 && wineProfile.tannin > 5) {
    let s = -10 * (2 - qualityModifier);
    score += s;
    rationale.push({
      p: s.toFixed(0),
      r: `Umami Clash: Penalty adjusted by tannin quality.`,
    });
  }
  if (foodProfile.bitterness > 5 && wineProfile.tannin > 6) {
    let s = -7 * (2 - qualityModifier);
    score += s;
    rationale.push({
      p: s.toFixed(0),
      r: `Bitter Collision: Penalty adjusted by tannin quality.`,
    });
  }

  // --- 2. Dynamic Temperature Modeling ---
  const tempDiff = Math.abs(wineProfile.currentTemp - wineProfile.idealTemp);
  if (tempDiff <= 1) {
    score += 3;
    rationale.push({
      p: "+3",
      r: "Temp Bonus: Wine is at its ideal temperature.",
    });
  } else if (tempDiff > 3) {
    let tempPenalty = -tempDiff * 1.5;
    score += tempPenalty;
    rationale.push({
      p: tempPenalty.toFixed(0),
      r: `Temp Penalty: Wine is ${tempDiff}°C from ideal, altering its profile.`,
    });
  }
  if (foodProfile.temp === "hot" && wineProfile.currentTemp < 10) {
    score -= 2;
    rationale.push({
      p: "-2",
      r: "Temp Contrast: Very cold wine with hot food can be jarring.",
    });
  }
  if (foodProfile.temp === "chilled" && wineProfile.currentTemp > 16) {
    score -= 2;
    rationale.push({
      p: "-2",
      r: "Temp Contrast: Warm wine with a chilled dish is unbalanced.",
    });
  }

  // --- 3. Aromatic Bridge Logic ---
  const sharedAromas = foodProfile.aromaCompounds.filter((c) =>
    wineProfile.aromaCompounds.includes(c)
  );
  if (sharedAromas.length > 0) {
    let aromaBonus = sharedAromas.length * 4;
    score += aromaBonus;
    rationale.push({
      p: `+${aromaBonus}`,
      r: `Aromatic Bridge: Shared compounds (${sharedAromas.join(
        ", "
      )}) create harmony.`,
    });
  }

  // --- 4. Problem Ingredient Solver ---
  if (foodProfile.problemIngredients.includes("cynarin")) {
    let cynarinPenalty = -25;
    let mitigationFound = false;
    // Check for mitigating factors
    if (foodProfile.cookingMethod === "frying" || foodProfile.fat > 6) {
      cynarinPenalty += 15; // Frying/fat significantly reduces the effect
      mitigationFound = true;
      rationale.push({
        p: "+15",
        r: "Cynarin Mitigated: Fat from frying reduces the negative sweetening effect.",
      });
    }
    if (wineProfile.acidity > 8 && wineProfile.sweetness <= 1) {
      cynarinPenalty += 10; // Bone-dry, high-acid wines are the best defense
      mitigationFound = true;
      rationale.push({
        p: "+10",
        r: "Cynarin Defense: Bone-dry, high-acid wine resists the sweetening effect.",
      });
    }
    score += cynarinPenalty;
    if (!mitigationFound) {
      rationale.push({
        p: "-25",
        r: "Problem Ingredient: Artichoke (cynarin) will make most wines taste unpleasantly sweet.",
      });
    }
  }

  // --- Final Interpretation ---
  let interpretation = "",
    colorClass = "";
  if (score > 15) {
    interpretation = "Excellent Pairing";
    colorClass = "text-green-400";
  } else if (score >= 5) {
    interpretation = "Good Pairing";
    colorClass = "text-blue-400";
  } else if (score > -5) {
    interpretation = "Neutral / Risky";
    colorClass = "text-yellow-400";
  } else {
    interpretation = "Problematic Pairing";
    colorClass = "text-red-400";
  }

  return { score, interpretation, rationale, colorClass };
};

// --- Main Application Component ---
export default function V5StandaloneDemo() {
  const [selectedFoodId, setSelectedFoodId] = useState(menuItemsData[0].id);
  const [selectedWineId, setSelectedWineId] = useState(winesData[0].id);
  const [liveWineProfile, setLiveWineProfile] = useState(winesData[0].profile);

  const handleFoodChange = (id) => {
    setSelectedFoodId(id);
  };

  const handleWineChange = (id) => {
    const newWine = winesData.find((w) => w.id === id);
    setSelectedWineId(id);
    setLiveWineProfile(newWine.profile);
  };

  const handleTempChange = (temp) => {
    setLiveWineProfile((prev) => ({ ...prev, currentTemp: temp }));
  };

  const selectedFood = useMemo(
    () => menuItemsData.find((item) => item.id === selectedFoodId),
    [selectedFoodId]
  );

  const pairingResult = useMemo(
    () => getAdvancedPairingScore_v5(selectedFood.profile, liveWineProfile),
    [selectedFood, liveWineProfile]
  );

  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500">
            SommelAI Engine v5
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            The Ultimate Dining Experience Simulator
          </p>
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg mt-4 inline-block">
            Standalone Demo - Pure v5 Algorithm
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* --- Input Selections --- */}
          <div className="space-y-8">
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">
                Select a Dish
              </h2>
              <select
                value={selectedFoodId}
                onChange={(e) => handleFoodChange(Number(e.target.value))}
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {menuItemsData.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">
                Select a Wine
              </h2>
              <select
                value={selectedWineId}
                onChange={(e) => handleWineChange(Number(e.target.value))}
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-purple-500 focus:border-purple-500"
              >
                {winesData.map((wine) => (
                  <option key={wine.id} value={wine.id}>
                    {wine.name}
                  </option>
                ))}
              </select>
              <div className="mt-4">
                <label
                  className="text-sm font-medium text-gray-300"
                  htmlFor="temp-slider"
                >
                  Adjust Serving Temp (°C)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    id="temp-slider"
                    type="range"
                    min="5"
                    max="20"
                    value={liveWineProfile.currentTemp}
                    onChange={(e) => handleTempChange(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-600 rounded-lg cursor-pointer"
                  />
                  <span className="font-bold text-lg text-yellow-400">
                    {liveWineProfile.currentTemp}°
                  </span>
                </div>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Ideal: {liveWineProfile.idealTemp}°C
                </p>
              </div>
            </div>
          </div>

          {/* --- Results Section --- */}
          <div className="bg-gray-800 p-6 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-semibold mb-4 text-center">
              Pairing Analysis
            </h2>
            <div className="text-center my-6">
              <p className={`text-7xl font-bold ${pairingResult.colorClass}`}>
                {pairingResult.score.toFixed(0)}
              </p>
              <p
                className={`text-xl font-semibold mt-2 ${pairingResult.colorClass}`}
              >
                {pairingResult.interpretation}
              </p>
            </div>
            <h3 className="text-lg font-semibold mb-3 border-t border-gray-700 pt-4">
              Rationale Breakdown
            </h3>
            <div className="space-y-3 text-sm max-h-80 overflow-y-auto pr-2">
              {pairingResult.rationale.map((item, index) => (
                <div key={index} className="flex items-start">
                  <span
                    className={`w-12 text-center font-bold mr-3 ${
                      item.p.startsWith("+") ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {item.p}
                  </span>
                  <p className="text-gray-300 flex-1">{item.r}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Feature Overview */}
        <div className="mt-8 bg-gray-800 p-6 rounded-2xl shadow-lg">
          <h3 className="text-xl font-semibold mb-4 text-indigo-400">
            v5 Algorithm Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="bg-indigo-900/30 p-3 rounded-lg">
              <h4 className="font-semibold text-indigo-300">
                Tannin Quality Index
              </h4>
              <p className="text-gray-300 mt-1">
                Quality modifier:{" "}
                {(liveWineProfile.tanninQuality * 100).toFixed(0)}%
              </p>
            </div>
            <div className="bg-purple-900/30 p-3 rounded-lg">
              <h4 className="font-semibold text-purple-300">
                Temperature Modeling
              </h4>
              <p className="text-gray-300 mt-1">
                Current vs Ideal: {liveWineProfile.currentTemp}°C vs{" "}
                {liveWineProfile.idealTemp}°C
              </p>
            </div>
            <div className="bg-teal-900/30 p-3 rounded-lg">
              <h4 className="font-semibold text-teal-300">Aromatic Bridge</h4>
              <p className="text-gray-300 mt-1">
                Food: {selectedFood.profile.aromaCompounds.join(", ")}
              </p>
              <p className="text-gray-300">
                Wine: {liveWineProfile.aromaCompounds.join(", ")}
              </p>
            </div>
            <div className="bg-red-900/30 p-3 rounded-lg">
              <h4 className="font-semibold text-red-300">
                Problem Ingredients
              </h4>
              <p className="text-gray-300 mt-1">
                {selectedFood.profile.problemIngredients.length > 0
                  ? selectedFood.profile.problemIngredients.join(", ")
                  : "None detected"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
